/**
 * Holiday Utilities
 * Computes upcoming US holidays and observances for store closures
 */

export interface Holiday {
  date: Date;
  name: string;
  type: 'federal' | 'cultural' | 'observance';
}

/**
 * Get the nth occurrence of a day in a month
 * Used for holidays like Memorial Day (last Monday of May) and Thanksgiving (4th Thursday of November)
 */
const getNthDayOfMonth = (year: number, month: number, dayOfWeek: number, n: number): Date => {
  const date = new Date(year, month, 1);

  // Find the first occurrence of the day
  while (date.getDay() !== dayOfWeek) {
    date.setDate(date.getDate() + 1);
  }

  // Move to nth occurrence
  date.setDate(date.getDate() + (n - 1) * 7);

  return date;
};

/**
 * Get the last occurrence of a day in a month
 */
const getLastDayOfMonth = (year: number, month: number, dayOfWeek: number): Date => {
  // Start from the end of the month
  const date = new Date(year, month + 1, 0);

  // Move backwards to find the last occurrence
  while (date.getDay() !== dayOfWeek) {
    date.setDate(date.getDate() - 1);
  }

  return date;
};

/**
 * Calculate Hanukkah start date for a given year
 * Hanukkah starts on 25 Kislev in the Hebrew calendar
 * This is an approximation - for exact dates, a Hebrew calendar library would be needed
 */
const getHanukkahStart = (year: number): Date => {
  // Hanukkah dates vary but generally fall between late November and late December
  // These are approximate Gregorian dates for Hanukkah start (first candle night)
  const hanukkahDates: Record<number, { month: number; day: number }> = {
    2024: { month: 11, day: 25 }, // Dec 25, 2024
    2025: { month: 11, day: 14 }, // Dec 14, 2025
    2026: { month: 11, day: 4 },  // Dec 4, 2026
    2027: { month: 11, day: 24 }, // Dec 24, 2027
    2028: { month: 11, day: 12 }, // Dec 12, 2028
    2029: { month: 11, day: 1 },  // Dec 1, 2029
    2030: { month: 11, day: 20 }, // Dec 20, 2030
  };

  const dateInfo = hanukkahDates[year];
  if (dateInfo) {
    return new Date(year, dateInfo.month, dateInfo.day);
  }

  // Fallback: approximate based on typical range (mid-December)
  return new Date(year, 11, 15);
};

/**
 * Generate holidays and observances for a given year
 */
export const getHolidays = (year: number): Holiday[] => {
  const holidays: Holiday[] = [];

  // Federal Holidays
  holidays.push(
    { date: new Date(year, 0, 1), name: "New Year's Day", type: 'federal' },
    { date: getNthDayOfMonth(year, 0, 1, 3), name: "Martin Luther King Jr. Day", type: 'federal' },
    { date: getNthDayOfMonth(year, 1, 1, 3), name: "Presidents' Day", type: 'federal' },
    { date: getLastDayOfMonth(year, 4, 1), name: "Memorial Day", type: 'federal' },
    { date: new Date(year, 5, 19), name: "Juneteenth", type: 'federal' },
    { date: new Date(year, 6, 4), name: "Independence Day", type: 'federal' },
    { date: getNthDayOfMonth(year, 8, 1, 1), name: "Labor Day", type: 'federal' },
    { date: getNthDayOfMonth(year, 9, 1, 2), name: "Columbus Day", type: 'federal' },
    { date: new Date(year, 10, 11), name: "Veterans Day", type: 'federal' },
    { date: getNthDayOfMonth(year, 10, 4, 4), name: "Thanksgiving", type: 'federal' },
    { date: new Date(year, 11, 25), name: "Christmas Day", type: 'federal' }
  );

  // Common Observances (not federal holidays but commonly observed)
  holidays.push(
    { date: new Date(year, 11, 24), name: "Christmas Eve", type: 'observance' },
    { date: new Date(year, 11, 31), name: "New Year's Eve", type: 'observance' },
    { date: getNthDayOfMonth(year, 10, 4, 4).getDate() + 1 <= 30
      ? new Date(year, 10, getNthDayOfMonth(year, 10, 4, 4).getDate() + 1)
      : new Date(year, 11, 1), name: "Day After Thanksgiving", type: 'observance' }
  );

  // Fix Day After Thanksgiving calculation
  const thanksgiving = getNthDayOfMonth(year, 10, 4, 4);
  const dayAfterThanksgiving = new Date(thanksgiving);
  dayAfterThanksgiving.setDate(thanksgiving.getDate() + 1);
  // Remove the incorrectly calculated one and add correct one
  const daftIndex = holidays.findIndex(h => h.name === "Day After Thanksgiving");
  if (daftIndex !== -1) {
    holidays[daftIndex] = { date: dayAfterThanksgiving, name: "Day After Thanksgiving", type: 'observance' };
  }

  // Cultural/Religious Holidays
  const hanukkahStart = getHanukkahStart(year);
  holidays.push(
    { date: hanukkahStart, name: "Hanukkah (First Night)", type: 'cultural' }
  );

  // Easter Sunday (Western) - Calculate using algorithm
  const easter = calculateEaster(year);
  holidays.push(
    { date: easter, name: "Easter Sunday", type: 'cultural' }
  );

  // Mother's Day - Second Sunday in May
  holidays.push(
    { date: getNthDayOfMonth(year, 4, 0, 2), name: "Mother's Day", type: 'observance' }
  );

  // Father's Day - Third Sunday in June
  holidays.push(
    { date: getNthDayOfMonth(year, 5, 0, 3), name: "Father's Day", type: 'observance' }
  );

  return holidays;
};

/**
 * Calculate Easter Sunday using the Anonymous Gregorian algorithm
 */
const calculateEaster = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month, day);
};

/**
 * Get upcoming holidays from now to X months in the future
 */
export const getUpcomingHolidays = (monthsAhead: number = 6): Holiday[] => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const futureDate = new Date(now);
  futureDate.setMonth(futureDate.getMonth() + monthsAhead);

  const holidays: Holiday[] = [];

  // Get holidays for current year and next year (to handle year transitions)
  const currentYear = now.getFullYear();
  const yearsToCheck = [currentYear, currentYear + 1];

  for (const year of yearsToCheck) {
    const yearHolidays = getHolidays(year);
    for (const holiday of yearHolidays) {
      // Only include holidays that are in the future and within our window
      if (holiday.date >= now && holiday.date <= futureDate) {
        holidays.push(holiday);
      }
    }
  }

  // Sort by date
  holidays.sort((a, b) => a.date.getTime() - b.date.getTime());

  return holidays;
};

/**
 * Check if a date matches a holiday (ignoring time)
 */
export const isHolidayDate = (date: Date, holidays: Holiday[]): Holiday | undefined => {
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);

  return holidays.find(h => {
    const holidayDate = new Date(h.date);
    holidayDate.setHours(0, 0, 0, 0);
    return holidayDate.getTime() === checkDate.getTime();
  });
};

/**
 * Filter out holidays that already exist in the closures list
 */
export const filterNewHolidays = (
  upcomingHolidays: Holiday[],
  existingClosures: Array<{ date: Date; reason: string }>
): Holiday[] => {
  return upcomingHolidays.filter(holiday => {
    const holidayDate = new Date(holiday.date);
    holidayDate.setHours(0, 0, 0, 0);

    return !existingClosures.some(closure => {
      const closureDate = new Date(closure.date);
      closureDate.setHours(0, 0, 0, 0);
      return closureDate.getTime() === holidayDate.getTime();
    });
  });
};

/**
 * Format a date for display (e.g., "Mon, Dec 25")
 */
export const formatHolidayDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Get days until a holiday
 */
export const getDaysUntil = (date: Date): number => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Get holiday type label
 */
export const getHolidayTypeLabel = (type: Holiday['type']): string => {
  switch (type) {
    case 'federal': return 'Federal Holiday';
    case 'cultural': return 'Cultural';
    case 'observance': return 'Observance';
    default: return '';
  }
};
